import React, { useEffect, useState } from 'react';
import { compose } from 'ramda';
import { makeStyles } from '@material-ui/core';
import {
  reduxForm,
  Form,
  Field,
  InjectedFormProps,
  isValid,
  getFormValues
} from 'redux-form';
import Fields from 'components/Fields';
import { Button, BoxWrapper, Modal } from 'components/Base';
import Store from 'store/store';
import { PastPerformanceType } from 'store/modules/InvestmentAnalyser/types';
import api from 'api';
import { errHelper } from 'utils';
import { CachedDataType } from 'store/modules/InvestmentAnalyser/types';
import BackgroundCharts from './BackgroundCharts';
import ReportOptions from 'utils/ReportOptions';
import { getAllPerformanceData } from 'components/PastPerformance/utils';

const { selectors } = Store;

const useStyles = makeStyles({
  paper: {
    minWidth: '20rem'
  },
  form: {
    padding: '1rem'
  },
  btnBox: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '16px'
  },
  field: {
    margin: '1rem 0'
  },
  checkboxLabel: {
    fontSize: '10px'
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    fontSize: '0.8rem',
    lineHeight: '1rem',
    color: '#747474'
  },
  itemRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  }
});

const { connect } = Store;

interface DownloadModalProps {
  isModalOpen: boolean;
  isSaving: boolean;
  onClose: Function;
  values: any;
  isValid: boolean;
  selectedInvestment: any;
  investments: any;
  pastPerformance: PastPerformanceType;
  pastPerformanceCacheIsValid: boolean;
  updatePastPerformance: Function;
  reportImages: any;
  cachedAssetAllocations: any;
  assetAllocationCacheIsValid: any;
  setCachedAssetAllocations: Function;
  saveNewResearchItem: Function;
  formValues: any;
  riskRatings: CachedDataType;
  scatterChartData: any;
}

const DownloadModal = ({
  onClose,
  isModalOpen,
  investments,
  reportImages,
  saveNewResearchItem,
  formValues,
  riskRatings,
  scatterChartData,
  pastPerformance
}: InjectedFormProps & DownloadModalProps) => {
  const classes = useStyles();
  const [loadingReport, setLoadingReport] = useState(false);
  const [backgroundChartsLoaded, setBackgroundChartsLoaded] = useState(false);

  const sections = [
    {
      items: ['Overview', 'Charges']
    },
    {
      title: 'Performance',
      items: [
        'Performance chart',
        'Cumulative performance table',
        'Discrete performance table',
        'Annualised performance table',
        'Calendar performance table'
      ]
    },
    {
      title: 'Risk',
      items: ['Risk score', 'Scatter chart', 'Max drawdown']
    },
    {
      title: 'Breakdown',
      items: ['Asset class', 'Sector', 'Country', 'Regional']
    },
    {
      title: 'Ratio',
      items: ['One Year', 'Three Years', 'Five Years']
    },
    {
      title: 'Model portfolio view',
      items: ['Show underlying funds']
    }
  ];

  const [reportSections, setReportSections] = useState([]);

  useEffect(() => {
    setBackgroundChartsLoaded(false);
  }, [isModalOpen]);

  const printOptions = (type: string) => {
    setLoadingReport(true);
    const reportName = formValues.name;
    return api
      .downloadReport({
        type: type,
        report_sections: Object.values(reportSections)
          .filter((item: any) => item.isSelected)
          .map((item: any) => item.name),
        investments: investments,
        risk_ratings: riskRatings,
        report_name: reportName,
        scatter_chart_data: scatterChartData,
        performance_data: getAllPerformanceData(pastPerformance.tabularData),
        ...reportImages
      })
      .then((response: any) => {
        saveNewResearchItem(reportName);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const filename = reportName
          ? reportName
          : 'Investment Analyser Report.pdf';
        link.setAttribute('download', `${filename}.pdf`);
        document.body.appendChild(link);
        link.click();
        setLoadingReport(false);
        onClose();
      })
      .catch((err: any) => {
        setLoadingReport(false);
        errHelper(err);
      });
  };

  return (
    <Modal isOpen={isModalOpen} closeModalFunc={() => onClose()}>
      <BoxWrapper
        className={classes.paper}
        title="Download Analysis"
        closeModal={() => onClose()}
      >
        <Form className={classes.form}>
          <div style={{ maxHeight: '65vh', overflow: 'auto' }}>
            <ReportOptions
              sections={sections}
              setSelectedSections={setReportSections}
              forceShowUnderlyingFunds
            />
          </div>
          <Field
            name={`name`}
            fullWidth
            className={classes.field}
            component={Fields.Text}
            label="Report Name"
          />

          <div className={classes.btnBox}>
            <Button
              style={{
                width: '100%'
              }}
              onClick={() => {
                printOptions('pdf');
              }}
              disabled={loadingReport || !backgroundChartsLoaded}
              isLoading={loadingReport || !backgroundChartsLoaded}
            >
              Download as PDF
            </Button>
          </div>
        </Form>
      </BoxWrapper>
      {isModalOpen ? (
        <BackgroundCharts
          setBackgroundChartsLoaded={setBackgroundChartsLoaded}
        />
      ) : (
        <></>
      )}
    </Modal>
  );
};

export default compose(
  reduxForm({
    form: 'downloadModal'
  }),
  connect((state: any) => ({
    investments:
      selectors.investmentAnalyserSelectors.checkedResearchInvestments(state),
    pastPerformance:
      selectors.investmentAnalyserSelectors.pastPerformance(state),
    riskRatings: selectors.investmentAnalyserSelectors.riskRatings(state),
    isValid: isValid('downloadModal')(state),
    reportImages: selectors.reportSelectors.getImages(state),
    formValues: getFormValues('downloadModal')(state),
    scatterChartData:
      selectors.investmentAnalyserSelectors.scatterChartData(state)
  })),
  reduxForm({
    form: 'downloadModal',
    enableReinitialize: true,
    asyncBlurFields: []
  })
)(DownloadModal);
